import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

const DEFAULT_ROW_HEIGHT = 28;
/** The implicit row "type" when the caller doesn't pass `rowType` — every item is then treated as one single type. */
const DEFAULT_ROW_TYPE = "__default__";

/**
 * Renders only the rows within (plus a small overscan around) the current
 * scroll position, rather than every item as a real DOM node up front. Used
 * by ElementBrowser.tsx as one instance for its whole tree (category/kind
 * headers and item rows alike) so the panel has exactly one scrollbar.
 *
 * Row height isn't hardcoded from CSS (`rem` scales with root font
 * size/zoom) — each distinct `rowType` measures itself once, from its own
 * first rendered row, via `getBoundingClientRect`, and that height is then
 * used for every row of that type. Callers with only one kind of row can
 * omit `rowType` entirely.
 *
 * `role="list"`/`"listitem"` instead of a literal `<ul>`/`<li>` since the
 * rows sit behind an absolutely-positioned offset wrapper, not valid as a
 * plain `<ul>`'s child.
 */
export function VirtualList<T>({
  items,
  height,
  overscan = 8,
  renderItem,
  className,
  rowType,
  getKey,
}: {
  items: T[];
  /** The container's real, fixed height — not an upper bound. A `max-height`
   * would let the box shrink to fit a collapsed tree's handful of visible
   * header rows instead of filling the sidebar, leaving dead space below
   * and a too-short scrollbar. */
  height: number;
  overscan?: number;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
  /** Groups items into named row "types" that may render at different
   * heights (category headers vs. kind headers vs. item rows), each
   * measured independently. Omit when every row is the same shape. */
  rowType?: (item: T, index: number) => string;
  /** A stable per-row React key — omit only when `items` genuinely has no
   * natural identity (e.g. a raw points array a caller hasn't assigned its
   * own synthetic ids to yet), since the index fallback below breaks focus
   * restore/measurement identity across a reorder or a mid-list insert. */
  getKey?: (item: T, index: number) => string | number;
}) {
  const [scrollTop, setScrollTop] = useState(0);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const measuredTypes = useRef<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  const typeOf = (item: T, index: number) => (rowType ? rowType(item, index) : DEFAULT_ROW_TYPE);
  const heightOf = (type: string) => heights[type] ?? DEFAULT_ROW_HEIGHT;

  // cumulative[i] is row i's top offset; cumulative[items.length] is the
  // full scrollable height.
  const cumulative = useMemo(() => {
    const offsets = new Array<number>(items.length + 1);
    offsets[0] = 0;
    for (let i = 0; i < items.length; i++) {
      offsets[i + 1] = offsets[i] + heightOf(typeOf(items[i], i));
    }
    return offsets;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `typeOf`/`heightOf` close over `heights`/`rowType`, already covered by the deps below.
  }, [items, heights, rowType]);

  /** First row index whose span contains pixel offset `y` (or `items.length` past the end). */
  function indexAtOffset(y: number): number {
    let lo = 0;
    let hi = items.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (cumulative[mid + 1] <= y) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  const totalHeight = cumulative[items.length] ?? 0;
  const startIndex = items.length === 0 ? 0 : Math.max(0, indexAtOffset(scrollTop) - overscan);
  const endIndex =
    items.length === 0
      ? 0
      : Math.min(items.length, indexAtOffset(scrollTop + height) + 1 + overscan);
  const offsetY = cumulative[startIndex] ?? 0;
  const visibleItems = items.slice(startIndex, endIndex);

  function measureRow(node: HTMLDivElement | null, type: string) {
    if (!node || measuredTypes.current.has(type)) return;
    measuredTypes.current.add(type);
    const height = node.getBoundingClientRect().height;
    if (height > 0 && Math.abs(height - heightOf(type)) > 0.5) {
      setHeights((h) => ({ ...h, [type]: height }));
    }
  }

  // Focuses `focusIndex`'s row once mounted — the arrow-key handler below may
  // have just moved `scrollTop` to bring a previously-unmounted row into the
  // virtualized window, so it isn't guaranteed to exist in the DOM until the
  // resulting re-render lands.
  useEffect(() => {
    if (focusIndex === null || !containerRef.current) return;
    const row = containerRef.current.querySelector<HTMLElement>(`[data-index="${focusIndex}"]`);
    const focusable = row?.querySelector<HTMLElement>(
      "button, a[href], input, select, textarea, [tabindex]"
    );
    (focusable ?? row)?.focus();
  }, [focusIndex]);

  /**
   * ArrowUp/ArrowDown move focus one row at a time, scrolling it into the
   * virtualized window first if not currently mounted — plain Tab can never
   * reach a row outside `overscan` (it doesn't exist as a DOM node yet),
   * which would otherwise make every item past the first screen unreachable
   * by keyboard. Drives `scrollTop` state directly rather than only setting
   * `container.scrollTop` and waiting for the resulting native `scroll`
   * event — jsdom doesn't fire one for a programmatic scrollTop assignment,
   * which would make this untestable.
   */
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-index]");
    if (!row) return;
    const currentIndex = Number(row.dataset.index);
    const nextIndex = e.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    e.preventDefault();
    const rowTop = cumulative[nextIndex];
    const rowBottom = cumulative[nextIndex + 1];
    let nextScrollTop = scrollTop;
    if (rowTop < scrollTop) nextScrollTop = rowTop;
    else if (rowBottom > scrollTop + height) nextScrollTop = rowBottom - height;
    if (nextScrollTop !== scrollTop) {
      setScrollTop(nextScrollTop);
      if (containerRef.current) containerRef.current.scrollTop = nextScrollTop;
    }
    setFocusIndex(nextIndex);
  }

  return (
    <div
      ref={containerRef}
      className={className}
      role="list"
      style={{ height, overflowY: "auto" }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      onKeyDown={handleKeyDown}
    >
      <div style={{ height: totalHeight, position: "relative" }}>
        <div style={{ position: "absolute", top: offsetY, left: 0, right: 0 }}>
          {visibleItems.map((item, i) => {
            const index = startIndex + i;
            const type = typeOf(item, index);
            const needsMeasure = !measuredTypes.current.has(type);
            return (
              <div
                key={getKey ? getKey(item, index) : index}
                role="listitem"
                data-index={index}
                ref={needsMeasure ? (node) => measureRow(node, type) : undefined}
              >
                {renderItem(item, index)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
